import React from "react";
import { searchService } from "../../../services/searchService";
import { normalizeWebsite } from "../../../utils/geo";
import { sanitizedHtml } from "../../../utils/sanitize";

const PHONE_KEYS = [
  "phone_1", "phone_2", "phone_3", "phone_4", "phone_5",
  "phone_toll_free", "phone_fax", "phone_tty", "phone_hotline",
];

function extractPhones(agency) {
  // For agency card, only show phone_1 and phone_fax from agency_card_phones
  const phones = agency.agency_card_phones;
  if (!phones) return [];

  return ["phone_1", "phone_fax"]
    .map((key) => {
      const number = phones[key];
      if (!number) return null;
      const nameKey = `${key}_name`;
      const descKey = `${key}_description`;
      let phoneName = phones[nameKey] || key.replace("phone_", "").replace(/_/g, " ");
      if (phoneName.length <= 2) phoneName = `Telephone ${phoneName}`;
      return { number, name: phoneName, description: phones[descKey] || "" };
    })
    .filter(Boolean);
}

function AgencyInfo({ agencyId }) {
  const [agency, setAgency] = React.useState(null);

  React.useEffect(() => {
    if (!agencyId) return;
    searchService.getAgency(agencyId).then(setAgency).catch(() => {});
  }, [agencyId]);

  if (!agency) return null;

  const websiteLink = normalizeWebsite(agency.website || "");
  const phones = extractPhones(agency);

  const mailingComponents = [
    agency.mailing_street_1,
    agency.mailing_street_2,
    [agency.mailing_city, agency.mailing_state].filter(Boolean).join(", "),
    agency.mailing_postal_code,
  ].filter(Boolean);

  const physicalComponents = [
    agency.physical_street_1,
    agency.physical_street_2,
    [agency.physical_city, agency.physical_state].filter(Boolean).join(", "),
    agency.physical_postal_code,
  ].filter(Boolean);

  const sites = agency.sites || [];
  const agencyDetails = [
    mailingComponents.length > 0 ? {
      label: "Mailing Address",
      content: <div dangerouslySetInnerHTML={sanitizedHtml(mailingComponents.join("<br />"))} />,
    } : null,
    physicalComponents.length > 0 ? {
      label: "Physical Address",
      content: <div dangerouslySetInnerHTML={sanitizedHtml(physicalComponents.join("<br />"))} />,
    } : null,
    ...phones.map((phone) => ({
      label: phone.name,
      content: (
        <p>
          <a href={`tel:${phone.number.replace(/\D/g, "")}`}>{phone.number}</a>
          {phone.description && (
            <>
              <br />
              {phone.description}
            </>
          )}
        </p>
      ),
    })),
    agency.hours ? {
      label: "Hours",
      content: <p>{agency.hours}</p>,
    } : null,
    agency.website ? {
      label: "Website",
      content: (
        <p className="website-url">
          <a href={websiteLink} target="_blank" rel="noopener noreferrer">{agency.website}</a>
        </p>
      ),
    } : null,
    agency.last_verified_on ? {
      label: "Last Verified On",
      content: agency.last_verified_on,
    } : null,
    agency.legal_status ? {
      label: "Legal Status",
      content: agency.legal_status,
    } : null,
    agency.disabilities_access ? {
      label: "Disability Access",
      content: <div dangerouslySetInnerHTML={sanitizedHtml(agency.disabilities_access)} />,
    } : null,
  ].filter(Boolean);

  return (
    <section className="agency agency-region search-detail-agency">
      <h3 className="resource-name">Agency Info</h3>

      {agency.description && (
        <div className="description search-detail-agency-description">
          <label className="label">Description</label>
          <p className="resource-description" dangerouslySetInnerHTML={sanitizedHtml(agency.description)} />
        </div>
      )}

      {agencyDetails.length > 0 && (
        <div className="search-detail-agency-grid">
          {agencyDetails.map((item) => (
            <div className="search-detail-agency-item" key={item.label}>
              <h4>{item.label}</h4>
              <div className="search-detail-agency-item-body">{item.content}</div>
            </div>
          ))}
        </div>
      )}

      {sites.length > 0 && (
        <div className="search-detail-agency-locations">
          <h4>Agency Locations &amp; Programs</h4>
          <div className="search-detail-agency-sites">
            {sites.map((site, i) => (
              <div className="search-detail-agency-site" key={i}>
                {!site.private_physical_address && (
                  <div
                    className="site-address"
                    dangerouslySetInnerHTML={sanitizedHtml(
                      [site.name, site.address_1, site.address_2, [site.city, site.state].filter(Boolean).join(", "), site.postal_code]
                        .filter(Boolean)
                        .join("<br />")
                    )}
                  />
                )}
                <ul className="programs">
                  {(site.program_sites || []).map((ps) =>
                    ps.program ? (
                      <li className="program" key={ps.number || ps.id}>
                        <a className="program-name" href={`/search/${ps.number}`}>
                          {ps.program.name}
                        </a>
                      </li>
                    ) : null
                  )}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

export default AgencyInfo;
